<script setup lang="ts">
import { explainMonsterAffix, explainMonsterAttrsWeight } from '@/core/monster/affix'
import { useUserStore } from '@/stores/modules/user'
import type { NestMonsterType } from '@/types/monster'
import { socketEmitAsync } from '@/utils/web-socket'
import { QuestionFilled } from '@element-plus/icons-vue'
import { useRoomStore } from '@/stores/modules/room'

const userStore = useUserStore()
const roomStore = useRoomStore()

defineProps<{ plainMonster?: NestMonsterType }>()
const emits = defineEmits(['getMonster', 'start', 'showRoom'])

function handleHuntPlainMonster() {
  if (userStore.soulType <= 3) return ElMessage.warning('二觉后解锁')
  if (userStore.plainTimes <= 0) return ElMessage.warning('无奖励时无法进行探寻')
  if (userStore.checkPetCapacity) return ElMessage.warning('魔物牧场已满，无法进行探寻')

  ElMessageBox.confirm('<p class="affix">确定<span class="info">探寻</span>【<span class="pet">魔物旷野</span>】吗？</p>', '提示', {
    type: 'warning',
    appendTo: 'body',
    dangerouslyUseHTMLString: true,
    confirmButtonClass: 'is-plain',
  }).then(async () => {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'huntPlainMonster' })
    emits('getMonster', res)
    ElMessage.success('探寻成功')
  })
}

async function handleStartPlain() {
  if (userStore.soulType <= 3) return ElMessage.warning('二觉后解锁')
  if (userStore.checkPetCapacity) return ElMessage.warning('魔物牧场已满，无法进行战斗')
  if (roomStore.id) return start()
  ElMessageBox.confirm(
    '<p class="affix">确定<span class="hp">独自</span>进行【<span class="pet">魔物旷野</span>】<span class="info">战斗</span>吗？</p>',
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(() => start())

  async function start() {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'startPlainFight' })
    roomStore.clearRoomInfo()
    emits('start', { ...res, type: 'plain' })
  }
}

function handleRefreshPlain() {
  if (userStore.soulType <= 3) return ElMessage.warning('二觉后解锁')
  if (userStore.gold < 1000000) return ElMessage.warning('重新探寻需要 1,000,000金币')
  if (userStore.checkPetCapacity) return ElMessage.warning('魔物牧场已满，无法进行探寻')
  if (roomStore.id) return ElMessage.warning('无法在房间内时重新探寻魔物')
  ElMessageBox.confirm(
    '<p class="affix">确定<span class="hp">放弃</span>捕捉当前魔物并进行重新<span class="info">探寻</span>吗？（消耗 <span class="gold">1,000,000</span>金币）</p>',
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(async () => {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'refreshPlainMonster' })
    userStore.gold -= 1000000
    emits('getMonster', res)
    ElMessage.success('重新探寻成功')
  })
}

function handleOpenRoom() {
  if (userStore.soulType <= 3) return ElMessage.warning('二觉后解锁')
  if (userStore.checkPetCapacity) return ElMessage.warning('魔物牧场已满，无法进行战斗')
  emits('showRoom')
}
</script>

<template>
  <div class="border-wrap plain">
    <h4 class="pet">
      魔物旷野
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix" style="max-height: 60vh; overflow: auto">
            <h4>魔物旷野说明</h4>
            <p>1. 每次探寻会随机遭遇一只<span class="pet">3~5阶</span>魔物。</p>
            <p>2. 通过魔物旷野战斗将必定获得对应击败魔物，并奖励<span class="info">2~6 潜能石</span></p>
          </div>
        </el-popover>
      </div>
    </h4>
    <p>剩余次数：{{ userStore.plainTimes }}</p>
    <p>
      当前怪物：
      <el-popover placement="bottom" :width="300" trigger="click" popper-class="monster-detail" :disabled="!plainMonster?.name">
        <template #reference>
          <el-button size="small" type="danger" plain>
            <span v-show="plainMonster?.name">{{ plainMonster?.name }}</span>
            <span v-show="!plainMonster?.name">（暂未探寻）</span>
          </el-button>
        </template>
        <div class="monster-detail" v-if="plainMonster">
          <h3 style="margin: 5px 0">👾{{ plainMonster.name }}👾</h3>
          <p>魔物阶级：{{ plainMonster?.rank }}</p>
          <p>属性倾向：{{ explainMonsterAttrsWeight(plainMonster.attrsWeight) }}</p>
          <p>血量：{{ plainMonster.hp }}</p>
          <p>防御：{{ plainMonster.def }}</p>
          <p>闪避率：{{ plainMonster.evs }}%</p>
          <p>抗爆率：{{ plainMonster.crtdef }}%</p>
          <template v-if="plainMonster.special?.length">
            <p>特性：</p>
            <p v-for="item of explainMonsterAffix(plainMonster.special)" v-html="item"></p>
          </template>
        </div>
      </el-popover>
    </p>
    <div style="margin: 10px 0">
      <div v-show="!plainMonster?.name">
        <el-button type="warning" plain @click="handleHuntPlainMonster" :disabled="userStore.soulType <= 3">
          {{ userStore.soulType <= 3 ? '二觉后解锁' : '探寻' }}
        </el-button>
        <el-button type="primary" plain :disabled="userStore.soulType <= 3" @click="handleOpenRoom">组队战斗</el-button>
      </div>
      <div v-show="plainMonster?.name">
        <el-button type="success" plain @click="() => handleStartPlain()" :disabled="Boolean(roomStore.id)">独自战斗</el-button>
        <el-button type="primary" plain @click="handleOpenRoom">组队战斗</el-button>
        <el-button type="danger" plain @click="handleRefreshPlain">重新探寻</el-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.monster-detail {
  p {
    margin: 4px 0 !important;
  }
}
</style>
